import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styles from './PricingPage.module.css';

const PricingPage = () => {
  const [isAnnual, setIsAnnual] = useState(true);
  const navigate = useNavigate();

  const faqs = [
    {
      q: "Do I need to create an account to upgrade?",
      a: "No. Because LeanWeek-AI is local-first, your Premium status is tied directly to your secure Apple ID. You still don't need to give us your email or create a password."
    },
    {
      q: "If I generate a plan on Free, do I lose it if I make a mistake?",
      a: "The Free tier allows for one complete 7-day plan generation per week. If you need to constantly adjust your schedule or swap equipment daily, the Premium tier unlocks unlimited AI regenerations."
    },
    {
      q: "Can I cancel my subscription easily?",
      a: "Absolutely. Subscriptions are managed directly through your phone’s native App Store settings. You can cancel with one tap."
    },
    {
      q: "What happens to my data if I downgrade?",
      a: "You will never lose your locally stored data. You will retain your current week's plan, but you will lose access to premium export themes and unlimited AI refreshes for your next cycle."
    }
  ];

  return (
    <div className={styles.page}>
      {/* 1. Hero & Philosophy */}
      <section className={styles.hero}>
        <div className={styles.container}>
          <h1>Structured weeks. <br/><span>Simple pricing.</span></h1>
          <p className={styles.subtitle}>
            We don't believe in holding your data hostage or charging you for a bloated social feed. 
            LeanWeek-AI offers a powerful free tier to get you started, and an accessible premium tier 
            for those who want total control over their training architecture. 
            <strong> Join the waitlist today to lock in Premium for life.</strong>
          </p>
        </div>
      </section>

      {/* 2. Pricing Toggle & Cards */}
      <section className={styles.pricingSection}>
        <div className={styles.container}>
          
          <div className={styles.toggleWrapper}>
            <span className={!isAnnual ? styles.activeText : styles.inactiveText}>Monthly</span>
            <button 
              className={styles.toggleBtn} 
              onClick={() => setIsAnnual(!isAnnual)}
              aria-label="Toggle annual billing"
            >
              <div className={`${styles.toggleKnob} ${isAnnual ? styles.knobAnnual : styles.knobMonthly}`} />
            </button>
            <span className={isAnnual ? styles.activeText : styles.inactiveText}>
              Annually <span className={styles.saveBadge}>Save 27%</span>
            </span>
          </div>

          <div className={styles.cardGrid}>
            {/* Free Tier */}
            <div className={styles.card}>
              <div className={styles.cardHeader}>
                <h3>The Basic Week</h3>
                <div className={styles.price}>$0<span>/forever</span></div>
                <p>The perfect starting point for structured fat loss.</p>
              </div>
              <ul className={styles.featureList}>
                <li>Generate 1 custom 7-day plan per week</li>
                <li>Standard exercise library & guidance</li>
                <li>Basic daily check-ins & tracking</li>
                <li>Standard Share Cards</li>
                <li>Local-first privacy guarantee</li>
                <li>Auto HealthKit Syncing</li>
              </ul>
              <button 
                className={styles.secondaryBtn}
                onClick={() => navigate('/waitlist')}
              >
                Join the Waitlist
              </button>
            </div>

            {/* Premium Tier */}
            <div className={`${styles.card} ${styles.premiumCard}`}>
              <div className={styles.premiumBadge}>Early Adopter Offer</div>
              <div className={styles.cardHeader}>
                <h3>LeanWeek Premium</h3>
                <div className={styles.price}>
                  <span style={{ textDecoration: 'line-through', fontSize: '0.6em', opacity: 0.7, marginRight: '8px' }}>
                    ${isAnnual ? '35' : '4'}
                  </span>
                  $0<span>/lifetime</span>
                </div>
                <p>Total flexibility and advanced analytics. Free forever for early adopters.</p>
              </div>
              <ul className={styles.featureList}>
                <li><strong>Everything in Free, plus:</strong></li>
                <li>Unlimited AI plan generations & refreshes</li>
                <li>"Swap Exercise" functionality</li>
                <li>Generate elaborate exercise guidance</li>
                <li>Get AI driven overall insights</li>
                <li>PDF Exports</li>
              </ul>
              <button 
                className={styles.primaryBtn}
                onClick={() => navigate('/waitlist')}
              >
                Claim Free Lifetime Premium
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* 3. FAQ Section */}
      <section className={styles.faqSection}>
        <div className={styles.container}>
          <h2 className={styles.sectionTitle}>Common Questions</h2>
          <div className={styles.faqGrid}>
            {faqs.map((faq, index) => (
              <div key={index} className={styles.faqItem}>
                <h4>{faq.q}</h4>
                <p>{faq.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 4. Final CTA */}
      <section className={styles.finalCta}>
        <div className={styles.container}>
          <h2>Ready to architect your week?</h2>
          <p>Be the first to experience LeanWeek-AI. Sign up now to become an early adopter and lock in your premium features forever.</p>
          <button 
            className={styles.primaryBtnLarge}
            onClick={() => navigate('/waitlist')}
          >
            Join the waitlist for free premium lifetime access
          </button>
        </div>
      </section>
    </div>
  );
};

export default PricingPage;